import IconButton from "@material-ui/core/IconButton";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import React, { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Scrollbar } from "react-scrollbars-custom";
import { Column, Row, useTable } from "react-table";
import useNotificationBell from "./../../apihooks/useNotificationBell";
import { useAppContext } from "./../../context/AppCtxProvider";
import { mea_t, stringTemplate } from "./../../tplutils/utils";
import { IWorkFlow } from "./../../uicomponents/view/workflowTypes";
import { makeThemedStyles } from "./../../viewcomponents/utils";
import Loader from "./Loader";
const useStyles = () =>
  makeThemedStyles("", (theme) => ({
    loaderContainer: {
      height: "100px",
      background: "#ffff",
      position: "relative",
      borderRadius: "2em",
    },
    loading: {
      position: "absolute",
      top: "50%",
      left: "50%",
    },
  }))();
const WrapToScroll = (props: {
  children: React.ReactNode;
  wrap?: boolean;
  wrapHeight?: number;
}) => {
  const { wrap, wrapHeight } = props;
  const rtl = useAppContext((s) => s.rtl);
  if (wrap) {
    return (
      <div className="ScrolledArea">
        <Scrollbar
          style={{
            width: "100%",
            height: typeof wrapHeight !== "undefined" ? wrapHeight : "270px",
          }}
          noScrollX
          rtl={rtl}
        >
          {props.children}
        </Scrollbar>
      </div>
    );
  }
  return <>{props.children}</>;
};
const WrapToBody = (props: {
  children: React.ReactNode;
  wrap?: boolean;
  onHeight?: (value: number) => void;
}) => {
  const { wrap, onHeight } = props;
  const ref = useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    if (ref.current && onHeight) {
      onHeight(ref.current.offsetHeight);
    }
  }, [ref, ref.current]);
  if (wrap) {
    return (
      <div className="Body" ref={ref}>
        {props.children}
      </div>
    );
  }
  return <>{props.children}</>;
};
function WorkflowTable<
  T extends {
    id: string;
    workflow?: IWorkFlow;
  }
>(props: {
  data: T[];
  columns: Column<T>[];
  url?: string;
  classPrefix?: string;
  useScrollButtons?: boolean;
  focus?: boolean;
  shouldWrapToScroll?: boolean;
  shouldWrapToBody?: boolean;
  wrapHeight?: number;
  isLoading?: boolean;
  reloadData?: boolean;
  onHeight?: (value: number) => void;
  contentLoading?: boolean;
}) {
  const { shouldWrapToScroll, wrapHeight, useScrollButtons } = props;
  const { shouldWrapToBody } = props;
  const { classPrefix } = props;
  const { columns, data, focus, onHeight, reloadData } = props;
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data,
    });
  const classes = useStyles();
  const ctx = useAppContext();
  const history = useHistory();
  const ref = React.useRef<HTMLDivElement>(null);
  const { pathname, search } = useLocation();
  const getPeriodString = (newValue: string) => {
    const queryParams = new URLSearchParams(search);
    queryParams.set("period", newValue);
    queryParams.set("wfperiod", newValue);
    const URL = `/?${queryParams.toString()}`;
    return URL;
  };
  const { refresh: refreshNotificationBell } = useNotificationBell(false);
  const [notificationBellRefreshed, setNotificationBell] =
    React.useState(false);
  const openWFTasks = data
    ? data.filter((item) => item.workflow?.done === false)
    : [];
  if (
    notificationBellRefreshed === false &&
    data?.length > 0 &&
    openWFTasks.length === 0
  ) {
    // user has processed all WF tasks, refresh notification bell
    refreshNotificationBell();
    setNotificationBell(true);
  }
  const toSingleInstance = (row: Row<T>) => {
    let { url } = props;
    if (!url) return;
    if (row.original.workflow) {
      ctx.sendValue("wfInstanceId", row.original.workflow.wfInstanceId);
    } else ctx.wfInstanceId = undefined;
    url = stringTemplate(url, { ctx, item: row.original });
    if (
      props.classPrefix === "WorkflowKpi" ||
      props.classPrefix === "WorkflowProject"
    ) {
      url += getPeriodString(row?.original?.workflow?.period || "");
    }
    history.push(url);
  };
  useEffect(() => {
    if (reloadData && rows.length) toSingleInstance(rows[0]);
  }, [reloadData, rows]);
  const prefix = (str: string) => `${classPrefix}${str}`;
  if (props.isLoading) {
    return <Loader />;
  }
  return (
    <>
      {useScrollButtons && (
        <div className="ScrollButtonsRow">
          <div className="ScrollButtonsContainer" dir="ltr">
            <IconButton
              className="ScrollButton"
              onClick={() => {
                if (ref.current) ref.current.scrollLeft -= 450;
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <IconButton
              className="ScrollButton"
              onClick={() => {
                if (ref.current) ref.current.scrollLeft += 450;
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}
      <div
        {...getTableProps()}
        className={prefix("List")}
        style={{ opacity: props.isLoading ? 0.5 : 1 }}
        ref={ref}
      >
        {headerGroups.map((headerGroup) => (
          <div {...headerGroup.getHeaderGroupProps()} className="Heading">
            {headerGroup.headers.map((column) => (
              <div
                className={` ${prefix("ListHeadingCell")} ${prefix(
                  "ListCell"
                )} ${column.id}`}
                {...column.getHeaderProps()}
              >
                {column.render("Header")}
              </div>
            ))}
          </div>
        ))}
        {!props.contentLoading ? (
          <WrapToScroll wrap={shouldWrapToScroll} wrapHeight={wrapHeight}>
            <WrapToBody wrap={shouldWrapToBody} onHeight={onHeight}>
              {rows.map((row, rowIndex) => {
                prepareRow(row);
                const rowProps = row.getRowProps();
                row.cells[1].value = mea_t(
                  "Name",
                  row.cells[1].value,
                  ctx.language
                );
                return (
                  <div
                    {...{
                      ...rowProps,
                      className: `${prefix("ListRow")} ${
                        rowProps.className || ""
                      } ${
                        ctx.elementId === row.original.id &&
                        row?.original?.workflow?.wfInstanceId ===
                          ctx.wfInstanceId
                          ? "Selected"
                          : ""
                      }`,
                    }}
                    ref={(elem) => {
                      if (rowIndex === 0 && focus) {
                        elem?.focus();
                      }
                    }}
                    role="button"
                    // jsx-a11y/no-static-element-interactions
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        toSingleInstance(row);
                      }
                    }}
                    // jsx-a11y/no-static-element-interactions
                    tabIndex={0}
                    // jsx-a11y/no-static-element-interactions
                    onClick={() => {
                      toSingleInstance(row);
                    }}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <div
                          {...cell.getCellProps()}
                          className={`${prefix("ListCell")} ${cell.column.id}`}
                        >
                          {cell.render("Cell")}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </WrapToBody>
          </WrapToScroll>
        ) : (
          <div className={classes.loaderContainer}>
            <Loader size={20} class={classes.loading} />
          </div>
        )}
      </div>
    </>
  );
}
export default WorkflowTable;
